import React from "react";
import {
  Calendar,
  CalendarX,
  Clock,
  CreditCard,
  Euro,
  Hourglass,
  Info,
  MapPin,
  User,
  XCircle,
} from "lucide-react";
import { Link } from "react-router";
import {
  Subscription,
  useCancelSubscriptionMutation,
} from "../../api/subscriptionApi.ts";
import {
  getInstrumentIcon,
  getTranslations,
  useSupportedInstruments,
} from "../../api/instrumentApi.ts";
import {
  useTeacherLocationsQuery,
  useTeacherQuery,
} from "../../api/teacherApi.ts";
import { useLanguageStore } from "../../stores/languageStore.ts";
import { weekdays } from "../Weekdays.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import ConfirmActionButton from "../ConfirmActionButton.tsx";
import { IconTooltip } from "../util/IconTooltip.tsx";

type DisplayStatus = Subscription["status"] | "CANCELLING";

function getDisplayStatus(subscription: Subscription): DisplayStatus {
  if (subscription.status === "ACTIVE" && subscription.endingAt) {
    return "CANCELLING";
  }
  return subscription.status;
}

function getLeftBorderClass(status?: DisplayStatus): string {
  switch (status) {
    case "INCOMPLETE":
      return "border-l-amber-400";
    case "ACCEPTED":
      return "border-l-soluud-secondary";
    case "ACTIVE":
      return "border-l-soluud-primary";
    case "CANCELLING":
      return "border-l-soluud-hint";
    case "CANCELLED":
      return "border-l-soluud-border";
    case "PAST_DUE":
      return "border-l-soluud-warning";
    default:
      return "border-l-soluud-border";
  }
}

function StatusPill({ status }: { status?: DisplayStatus }) {
  const base =
    "flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium leading-tight";

  switch (status) {
    case "INCOMPLETE":
      return (
        <span className={`${base} bg-amber-50 text-amber-700`}>
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" />
          <Trans>Pending</Trans>
        </span>
      );
    case "ACCEPTED":
      return (
        <span
          className={`${base} bg-soluud-secondary-light text-soluud-text-on-secondary`}
        >
          <span className="bg-soluud-secondary h-1.5 w-1.5 shrink-0 rounded-full" />
          <Trans>Awaiting Payment</Trans>
        </span>
      );
    case "ACTIVE":
      return (
        <span className={`${base} bg-soluud-primary/10 text-soluud-primary`}>
          <span className="bg-soluud-primary h-1.5 w-1.5 shrink-0 rounded-full" />
          <Trans>Active</Trans>
        </span>
      );
    case "CANCELLING":
      return (
        <span className={`${base} bg-soluud-hint/10 text-soluud-hint`}>
          <span className="bg-soluud-hint h-1.5 w-1.5 shrink-0 rounded-full" />
          <Trans>Cancelling</Trans>
        </span>
      );
    case "CANCELLED":
      return (
        <span
          className={`${base} bg-soluud-border-subtle text-soluud-text-tertiary`}
        >
          <span className="bg-soluud-text-tertiary h-1.5 w-1.5 shrink-0 rounded-full" />
          <Trans>Cancelled</Trans>
        </span>
      );
    case "PAST_DUE":
      return (
        <span className={`${base} bg-soluud-warning/10 text-soluud-warning`}>
          <span className="bg-soluud-warning h-1.5 w-1.5 shrink-0 rounded-full" />
          <Trans>Past Due</Trans>
        </span>
      );
    default:
      return null;
  }
}

interface UserSubscriptionCardProps {
  subscription: Subscription;
}

const UserSubscriptionCard: React.FC<UserSubscriptionCardProps> = ({
  subscription,
}) => {
  const { language } = useLanguageStore();
  const { data: supportedInstruments } = useSupportedInstruments();
  const { data: teacher } = useTeacherQuery(subscription.teacherId);
  const { data: teacherLocationsData } = useTeacherLocationsQuery(
    subscription.teacherId,
  );
  const { i18n, t } = useLingui();
  const queryClient = useQueryClient();
  const cancelMutation = useCancelSubscriptionMutation();
  const displayStatus = getDisplayStatus(subscription);

  const teacherLocations = teacherLocationsData?.locations;
  const location = teacherLocations?.find(
    (l) => l.id === subscription.locationId,
  );

  const InstrumentIcon = getInstrumentIcon(subscription.instrument);
  const translatedInstrument = getTranslations(
    supportedInstruments ?? [],
    subscription.instrument,
    language,
  );

  const weekday = weekdays.find((w) => w.value === subscription.dayOfWeek);
  const translatedDay = weekday
    ? i18n._(weekday.label)
    : subscription.dayOfWeek;

  const teacherName = teacher
    ? `${teacher.firstName} ${teacher.lastName}`
    : undefined;

  const fromTime = subscription.from?.substring(0, 5) ?? subscription.from;
  const toTime = subscription.to?.substring(0, 5) ?? subscription.to;

  const handleCancel = () => {
    cancelMutation.mutate(subscription.id, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["user-subscriptions"] });
      },
    });
  };

  const isPreActive =
    subscription.status === "INCOMPLETE" || subscription.status === "ACCEPTED";
  const canCancel =
    isPreActive || (subscription.status === "ACTIVE" && !subscription.endingAt);
  const showCheckout = subscription.status === "ACCEPTED";
  const hasActions = canCancel || showCheckout;
  const cancelActionLabel = isPreActive
    ? t({ id: "Withdraw", message: "Withdraw" })
    : t({ id: "Cancel", message: "Cancel" });

  const isMuted = displayStatus === "CANCELLED";

  return (
    <div
      className={`bg-soluud-background-card border-soluud-border flex flex-col gap-2.5 rounded-xl border border-l-4 px-4 py-3 shadow-[--shadow-card] transition-shadow duration-200 hover:shadow-[--shadow-card-hover] ${getLeftBorderClass(displayStatus)} ${isMuted ? "opacity-60" : ""}`}
    >
      {/* Row 1: Instrument + status pill */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <InstrumentIcon className="text-soluud-primary h-4 w-4 shrink-0" />
          <span className="text-soluud-text-primary truncate text-sm font-semibold">
            {translatedInstrument || subscription.instrument}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {isPreActive && (
            <IconTooltip
              icon={Hourglass}
              iconClassName="h-3.5 w-3.5 text-amber-500 animation-bounce"
              text={
                subscription.status === "INCOMPLETE" ? (
                  <Trans>
                    The subscription will be cancelled if the teacher does not
                    accept it within 48 hours.
                  </Trans>
                ) : subscription.status === "ACCEPTED" ? (
                  <Trans>
                    The subscription will be cancelled if you do not add payment
                    data within 48 hours.
                  </Trans>
                ) : (
                  subscription.status
                )
              }
            />
          )}
          <StatusPill status={displayStatus} />
        </div>
      </div>

      {/* Row 2: Teacher · Location · Schedule */}
      <div className="text-soluud-text-secondary flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs">
        {/* Teacher */}
        <span className="flex items-center gap-1.5">
          {teacher?.profilePictureUrl ? (
            <img
              src={teacher.profilePictureUrl}
              alt=""
              className="h-3.5 w-3.5 shrink-0 rounded-full object-cover"
            />
          ) : (
            <User className="h-3 w-3 shrink-0 opacity-50" />
          )}
          {teacher ? (
            <Link
              to={`/profile/${subscription.teacherId}`}
              className="hover:text-soluud-primary hover:underline"
            >
              {teacherName}
            </Link>
          ) : (
            <span className="text-soluud-text-tertiary">…</span>
          )}
        </span>

        <span className="text-soluud-text-tertiary hidden sm:inline">·</span>

        {/* Location */}
        <span className="flex items-center gap-1">
          <MapPin className="h-3 w-3 shrink-0 opacity-50" />
          <span className="truncate">{location?.name ?? "…"}</span>
        </span>

        <span className="text-soluud-text-tertiary hidden sm:inline">·</span>

        {/* Day + time */}
        <span className="flex items-center gap-1">
          <Clock className="h-3 w-3 shrink-0 opacity-50" />
          {translatedDay}, {fromTime} – {toTime}
        </span>
      </div>

      {/* Row 3: Price + billing / ending date */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="flex items-center gap-1 text-xs">
          <Euro className="text-soluud-text-tertiary h-3 w-3 shrink-0" />
          {subscription.price != null ? (
            <>
              <span className="text-soluud-text-primary font-semibold">
                {subscription.price.toFixed(2)} €
              </span>
              <span className="text-soluud-text-tertiary">
                /<Trans>month</Trans>
              </span>
            </>
          ) : (
            <span className="text-soluud-text-tertiary italic">
              <Trans>TBD</Trans>
            </span>
          )}
        </span>

        {subscription.endingAt ? (
          <span className="text-soluud-hint flex items-center gap-1 text-xs">
            <CalendarX className="h-3 w-3 shrink-0" />
            {subscription.status === "CANCELLED" ? (
              <Trans>Ended</Trans>
            ) : (
              <Trans>Ends</Trans>
            )}
            &nbsp;
            {format(parseISO(subscription.endingAt), "PP")}
          </span>
        ) : (
          subscription.nextBillingAt &&
          subscription.status === "ACTIVE" && (
            <span className="text-soluud-text-secondary flex items-center gap-1 text-xs">
              <Calendar className="h-3 w-3 shrink-0 opacity-50" />
              <Trans>Next:</Trans>&nbsp;
              {format(new Date(subscription.nextBillingAt), "PP")}
            </span>
          )
        )}
      </div>

      {/* Cancellation reason */}
      {subscription.cancellationReason && (
        <div className="text-soluud-text-tertiary flex items-center gap-1.5 text-xs">
          <Info className="h-3 w-3 shrink-0" />
          {subscription.cancellationReason === "CANCELLED_BY_SUBSCRIBER" && (
            <Trans>Cancelled by you</Trans>
          )}
          {subscription.cancellationReason === "CANCELLED_BY_TEACHER" && (
            <Trans>Cancelled by teacher</Trans>
          )}
          {subscription.cancellationReason === "TEACHER_ACCEPTANCE_TIMEOUT" && (
            <Trans>Cancelled: teacher did not accept in time</Trans>
          )}
          {subscription.cancellationReason === "PAYMENT_TIMEOUT" && (
            <Trans>Cancelled: payment info not added in time</Trans>
          )}
        </div>
      )}

      {/* Actions footer */}
      {hasActions && (
        <div className="border-soluud-border-subtle flex items-center justify-between gap-2 border-t pt-2.5">
          {showCheckout ? (
            <Link
              to={`/subscriptions/${subscription.id}/checkout`}
              className="bg-soluud-primary hover:bg-soluud-primary-hover flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-white transition-colors"
            >
              <CreditCard className="h-3.5 w-3.5 shrink-0" />
              <Trans>Add Payment Info</Trans>
            </Link>
          ) : (
            <span />
          )}

          {canCancel && (
            <ConfirmActionButton
              onConfirm={handleCancel}
              isLoading={cancelMutation.isPending}
              confirmText={cancelActionLabel}
              cancelText={i18n._("Keep")}
              className="text-soluud-text-tertiary hover:bg-soluud-warning/5 hover:text-soluud-warning"
            >
              <XCircle className="h-3 w-3" />
              {cancelActionLabel}
            </ConfirmActionButton>
          )}
        </div>
      )}
    </div>
  );
};

export default UserSubscriptionCard;
